import { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2, Edit, CheckCircle, XCircle, Star, Search, Filter, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { serviceAPI } from '../../lib/api';
import { toast } from 'sonner';

const ServicesManagement = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [form, setForm] = useState({
    name: '', name_en: '', category: 'lawyer', location: '', location_en: '', email: '', phone: '', whatsapp: '',
    verified: false, fee_range: '', fee_range_en: '', bio: '', bio_en: ''
  });

  const fetchServices = useCallback(async () => {
    try {
      const res = await serviceAPI.getAllAdmin();
      setServices(res.data);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const resetForm = () => {
    setForm({
      name: '', name_en: '', category: 'lawyer', location: '', location_en: '', email: '', phone: '', whatsapp: '',
      verified: false, fee_range: '', fee_range_en: '', bio: '', bio_en: ''
    });
    setEditingService(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await serviceAPI.update(editingService.id, form);
        toast.success('সেবাদাতা আপডেট হয়েছে');
      } else {
        await serviceAPI.create(form);
        toast.success('সেবাদাতা যোগ হয়েছে');
      }
      fetchServices();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('কিছু সমস্যা হয়েছে');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setForm({
      name: service.name, name_en: service.name_en || '', category: service.category,
      location: service.location, location_en: service.location_en || '',
      email: service.email, phone: service.phone, whatsapp: service.whatsapp || '',
      verified: service.verified, fee_range: service.fee_range, fee_range_en: service.fee_range_en || '',
      bio: service.bio, bio_en: service.bio_en || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('এই সেবাদাতা মুছে ফেলতে চান?')) {
      try {
        await serviceAPI.delete(id);
        toast.success('সেবাদাতা মুছে ফেলা হয়েছে');
        fetchServices();
      } catch (err) {
        toast.error('মুছতে সমস্যা হয়েছে');
      }
    }
  };

  const handleToggleVerify = async (service) => {
    try {
      const res = await serviceAPI.toggleVerify(service.id);
      toast.success(res.data.verified ? 'যাচাই করা হয়েছে' : 'অযাচাই করা হয়েছে');
      fetchServices();
    } catch (err) {
      toast.error('স্ট্যাটাস পরিবর্তন করতে সমস্যা হয়েছে');
    }
  };

  const handleToggleVisibility = async (service) => {
    try {
      const res = await serviceAPI.toggleVisibility(service.id);
      toast.success(res.data.visible ? 'দৃশ্যমান করা হয়েছে' : 'লুকানো হয়েছে');
      fetchServices();
    } catch (err) {
      toast.error('স্ট্যাটাস পরিবর্তন করতে সমস্যা হয়েছে');
    }
  };

  const categoryLabels = { lawyer: 'উকিল', agent: 'এজেন্ট', document_assistant: 'ডকুমেন্ট সহকারী' };

  const filteredServices = services.filter(s => {
    const matchesSearch = searchQuery === '' ||
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.name_en && s.name_en.toLowerCase().includes(searchQuery.toLowerCase())) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || s.category === filterCategory;
    const matchesVerified = filterVerified === 'all' ||
      (filterVerified === 'verified' && s.verified) ||
      (filterVerified === 'unverified' && !s.verified);
    return matchesSearch && matchesCategory && matchesVerified;
  });

  const renderStars = (rating) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={12} className={i <= Math.round(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'} />
      ))}
      <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{rating > 0 ? rating.toFixed(1) : '-'}</span>
    </div>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white" data-testid="services-management-title">সেবাদাতা ম্যানেজমেন্ট</h1>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E7D32] hover:bg-[#1B5E20] w-full sm:w-auto" data-testid="add-service-btn">
              <Plus size={18} className="mr-2" /> নতুন সেবাদাতা
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingService ? 'সেবাদাতা সম্পাদনা' : 'নতুন সেবাদাতা'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">নাম (বাংলা)</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="service-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Name (English)</label>
                  <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="service-name-en-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">ক্যাটাগরি</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="service-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lawyer">উকিল</SelectItem>
                      <SelectItem value="agent">এজেন্ট</SelectItem>
                      <SelectItem value="document_assistant">ডকুমেন্ট সহকারী</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">অবস্থান (বাংলা)</label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required data-testid="service-location-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Location (English)</label>
                  <Input value={form.location_en} onChange={(e) => setForm({ ...form, location_en: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ইমেইল</label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="service-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">ফোন</label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="service-phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">WhatsApp নম্বর</label>
                  <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="8801XXXXXXXXX" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">ফি রেঞ্জ (বাংলা)</label>
                  <Input value={form.fee_range} onChange={(e) => setForm({ ...form, fee_range: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fee Range (English)</label>
                  <Input value={form.fee_range_en} onChange={(e) => setForm({ ...form, fee_range_en: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">বায়ো (বাংলা)</label>
                <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={2} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Bio (English)</label>
                <Textarea value={form.bio_en} onChange={(e) => setForm({ ...form, bio_en: e.target.value })} rows={2} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.verified} onCheckedChange={(v) => setForm({ ...form, verified: v })} />
                <label className="text-sm">যাচাইকৃত</label>
              </div>
              <Button type="submit" className="w-full bg-[#2E7D32] hover:bg-[#1B5E20]" data-testid="service-submit-btn">
                {editingService ? 'আপডেট করুন' : 'যোগ করুন'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="নাম, ইমেইল বা অবস্থান দিয়ে খুঁজুন..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="service-search-input" />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-40" data-testid="service-filter-category">
              <Filter size={14} className="mr-1" /><SelectValue placeholder="ক্যাটাগরি" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">সব ক্যাটাগরি</SelectItem>
              <SelectItem value="lawyer">উকিল</SelectItem>
              <SelectItem value="agent">এজেন্ট</SelectItem>
              <SelectItem value="document_assistant">ডকুমেন্ট সহকারী</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterVerified} onValueChange={setFilterVerified}>
            <SelectTrigger className="w-full sm:w-40" data-testid="service-filter-verified">
              <SelectValue placeholder="স্ট্যাটাস" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">সব স্ট্যাটাস</SelectItem>
              <SelectItem value="verified">যাচাইকৃত</SelectItem>
              <SelectItem value="unverified">অযাচাইকৃত</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{filteredServices.length} / {services.length} সেবাদাতা দেখাচ্ছে</p>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />)}
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block lg:hidden space-y-3">
            {filteredServices.map((service) => (
              <div key={service.id} className={`bg-white dark:bg-gray-800 rounded-xl p-4 border transition-colors ${service.visible === false ? 'border-red-200 dark:border-red-800/50 opacity-60' : 'border-gray-200 dark:border-gray-700'}`} data-testid={`service-card-${service.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{service.name}</p>
                    {service.name_en && <p className="text-xs text-gray-400">{service.name_en}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleToggleVisibility(service)} className={`p-1.5 rounded ${service.visible === false ? 'text-red-400' : 'text-green-600'}`} data-testid={`vis-toggle-m-${service.id}`}>
                      {service.visible === false ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(service)}><Edit size={16} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)}><Trash2 size={16} className="text-red-500" /></Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  <Badge variant="secondary" className="text-xs">{categoryLabels[service.category]}</Badge>
                  <button onClick={() => handleToggleVerify(service)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${service.verified ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                    {service.verified ? <CheckCircle size={10} /> : <XCircle size={10} />} {service.verified ? 'যাচাই' : 'অযাচাই'}
                  </button>
                  {service.visible === false && <Badge variant="destructive" className="text-xs">লুকানো</Badge>}
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>{service.location}</span>
                  <div className="flex items-center gap-1">
                    {renderStars(service.avg_rating || 0)}
                    <span>({service.total_reviews || 0})</span>
                  </div>
                </div>
              </div>
            ))}
            {filteredServices.length === 0 && <p className="text-center py-8 text-gray-400 text-sm">কোনো সেবাদাতা পাওয়া যায়নি</p>}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300">নাম</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300">ক্যাটাগরি</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300">রেটিং</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300">যাচাই</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300">দৃশ্যমানতা</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300">অ্যাকশন</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredServices.map((service) => (
                  <tr key={service.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${service.visible === false ? 'opacity-50' : ''}`} data-testid={`service-row-${service.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 dark:text-white text-sm">{service.name}</p>
                      {service.name_en && <p className="text-xs text-gray-400">{service.name_en}</p>}
                      <p className="text-xs text-gray-500 dark:text-gray-400">{service.email} | {service.location}</p>
                    </td>
                    <td className="px-4 py-3"><Badge variant="secondary" className="text-xs">{categoryLabels[service.category]}</Badge></td>
                    <td className="px-4 py-3">
                      {renderStars(service.avg_rating || 0)}
                      <span className="text-xs text-gray-400">{service.total_reviews || 0} রিভিউ</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleToggleVerify(service)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${service.verified ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400'}`} data-testid={`verify-toggle-${service.id}`}>
                        {service.verified ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        {service.verified ? 'যাচাই' : 'অযাচাই'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleToggleVisibility(service)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${service.visible === false ? 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400' : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400'}`} data-testid={`visibility-toggle-${service.id}`}>
                        {service.visible === false ? <EyeOff size={12} /> : <Eye size={12} />}
                        {service.visible === false ? 'লুকানো' : 'দৃশ্যমান'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(service)} data-testid={`edit-service-${service.id}`}><Edit size={16} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} data-testid={`delete-service-${service.id}`}><Trash2 size={16} className="text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
                {filteredServices.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">কোনো সেবাদাতা পাওয়া যায়নি</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ServicesManagement;
